import { useMutation } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { downloadFile } from 'utils/AppHelper'
import APIService from 'api/APIService'
import { JobStatus } from 'types/entity'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'

const useMergePreviewEmployees = (onSuccess?: VoidFunction) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const jobID = settings.pollingJobs.mergePreviewEmployeedJobId

  const {
    mutate: downloadPreview,
    status: downloadPreviewStatus,
    isPending: downloadingPreview
  } = useMutation({
    mutationFn: APIService.integrations.getDownloadPreviewIntegrationMembers,
    onSuccess: (data) => {
      set('pollingJobs', {
        ...settings.pollingJobs,
        mergePreviewEmployeedJobId: data.job_token_id
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  const loadingNotificationId = `merge-preview-employees-${jobID}`

  useQueryCallbacks({
    queryKey: ['merge-preview-employees', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      const errors = response?.error_list ?? []
      const hasErrors = errors.length > 0
      if (response.status === 'finished' && !hasErrors) {
        downloadFile(response.data.file_url as string)
        notification.success({
          title: `You have downloaded "Preview Members" file`,
          id: jobID
        })

        set('pollingJobs', {
          ...settings.pollingJobs,
          mergePreviewEmployeedJobId: ''
        })

        notification.dismiss(loadingNotificationId)
      }

      if (
        (response.status === 'in_progress' || response.status === 'enqueued') &&
        !hasErrors
      ) {
        notification.loading({
          id: loadingNotificationId,
          position: 'bottom-left',
          title: 'Exporting preview employees...',
          subtitle:
            'Downloading preview employees list, this may take a few minutes.',
          duration: Infinity
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && hasErrors)
      ) {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title:
            'There was an error while downloading the "Preview Members" file',
          subtitle: (
            <div>
              {response.error_list?.map((error) => {
                return <span key={error}>{error}</span>
              })}
            </div>
          ),
          id: jobID
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          mergePreviewEmployeedJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss(loadingNotificationId)
      notification.error({
        title: 'Could not export the "Preview Members" list',
        id: jobID
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        mergePreviewEmployeedJobId: ''
      })
    }
  })

  return {
    downloadPreview,
    downloadPreviewStatus,
    downloadingPreview
  }
}

export default useMergePreviewEmployees
